/**
 * 🖼️ Sprite + Skill VFX engine — shared across all combat surfaces
 * (BattlePage, WildsPartyCombat, PartyCombatModal).
 *
 * Public exports:
 *   • <SpriteFighter actor side role effect isEnemy variant />
 *       Renders the character portrait + transient skill VFX overlay.
 *       `variant` = "full" (default, 11rem tall) | "compact" (5rem inline)
 *   • <SkillVfxBurst category isUltimate />
 *       Standalone VFX overlay — usable on any container.
 *   • <SpriteStyles />
 *       Injects the keyframes ONCE per surface (mount it next to the
 *       sprite). Safe to mount multiple times (CSS rules are idempotent).
 *   • spriteUrlFor(actor, isEnemy)
 *   • spriteUrlForMob(mob)                 → enemy-side helper
 *   • skillVfxCategory(kind, statusId)     → driver for SkillVfxBurst
 *   • animClassFor(role, effect, isEnemy)  → card-level shake/lunge classes
 *   • flashClassFor(role, effect)          → red/cyan/emerald flash overlay
 */
import { useEffect, useState } from "react";

// ───────── Sprite asset registries ─────────
export const HERO_SPRITES = new Set([
  "warrior", "mage", "archer", "ash_rogue", "blue_mage", "david_lauer",
  "cassandra_auditor", "commoner_vanguard", "blood_practitioner",
  "glitch_architect", "war_librarian", "shadow_director", "lawman_brewer",
  "mother_phoenix", "celestial_vanguard", "scrap_alchemist",
  "afflicted_vanguard", "flesh_harvester", "fragile_prodigy", "weaver_of_fates",
]);

export const ENEMY_SPRITES = new Set([
  "training_dummy", "goblin", "ash_wraith", "gladiator", "boss_silhouette",
]);

export function spriteUrlFor(actor, isEnemy) {
  if (!actor) return null;
  if (isEnemy) return spriteUrlForMob(actor);
  const cid = actor.class_id;
  if (cid && HERO_SPRITES.has(cid)) return `/sprites/${cid}.png`;
  return null;
}

export function spriteUrlForMob(mob) {
  if (!mob) return null;
  const kind = (mob.kind || mob.mob_id || mob.name || "").toLowerCase();
  if (ENEMY_SPRITES.has(kind)) return `/sprites/${kind}.png`;
  if (kind.includes("dummy")) return "/sprites/training_dummy.png";
  if (kind.includes("goblin")) return "/sprites/goblin.png";
  if (kind.includes("wraith") || kind.includes("ash")) return "/sprites/ash_wraith.png";
  if (kind.includes("gladiator") || kind.includes("coliseum")) return "/sprites/gladiator.png";
  if (mob.is_map_boss || kind.includes("boss") || kind.includes("dragon") || kind.includes("queen")) {
    return "/sprites/boss_silhouette.png";
  }
  return null;
}

// ───────── Card-level CSS class helpers (existing keyframes in BattlePage) ─────────
export function animClassFor(role, effect, isEnemy) {
  if (!role || !effect) return "";
  if (role === "attacker") return isEnemy ? "attacker-lunge-down" : "attacker-lunge-up";
  if (role === "target") {
    if (effect.vfx?.includes("true")) return "target-hit-true";
    if (effect.vfx?.includes("crit")) return "target-hit-crit";
    return "target-hit-shake";
  }
  if (role === "target_self") {
    if (effect.kind === "heal") return "self-pulse-heal";
    if (effect.kind === "absorb") return "self-pulse-absorb";
  }
  return "";
}

export function flashClassFor(role, effect) {
  if (!role || !effect) return null;
  if (role === "target") {
    if (effect.vfx?.includes("true")) return "bg-fuchsia-500/30 animate-flash-quick";
    if (effect.vfx?.includes("crit")) return "bg-amber-400/25 animate-flash-quick";
    if (effect.vfx?.includes("super")) return "bg-orange-500/25 animate-flash-quick";
    return "bg-red-500/20 animate-flash-quick";
  }
  if (role === "target_self") {
    if (effect.kind === "heal") return "bg-emerald-500/20 animate-flash-quick";
    if (effect.kind === "absorb") return "bg-cyan-500/20 animate-flash-quick";
  }
  return null;
}

// ───────── Per-sprite motion class ─────────
export function spriteAnimFor(role, effect) {
  if (!role || !effect) return "sprite-idle";
  if (role === "attacker") return "sprite-attack-lunge";
  if (role === "target") {
    if (effect.vfx?.includes("true")) return "sprite-hurt-true";
    if (effect.vfx?.includes("crit")) return "sprite-hurt-crit";
    return "sprite-hurt";
  }
  if (role === "target_self") {
    if (effect.kind === "heal") return "sprite-heal-glow";
    if (effect.kind === "absorb") return "sprite-absorb-glow";
  }
  return "sprite-idle";
}

// ───────── Skill → VFX family ─────────
export function skillVfxCategory(kind, statusId) {
  if (kind === "heal" || kind === "absorb") return null;
  const s = (statusId || "").toLowerCase();
  if (s.includes("burn") || s.includes("ignite") || s.includes("ember") || s.includes("cinder")) return "fire";
  if (s.includes("smoke") || s.includes("shroud") || s.includes("ash")) return "smoke";
  if (s.includes("bleed") || s.includes("haemorrhage")) return "slash";
  if (s.includes("stun") || s.includes("shock")) return "lightning";
  if (kind === "ultimate") return "ultimate";
  if (kind === "atk_m") return "fire";
  if (kind === "atk_true") return "holy";
  if (kind === "leech") return "slash";
  return "slash";
}

const VFX_PRESETS = {
  fire:      { sigil: "🔥", aura: "bg-orange-500/40 blur-2xl", tint: "drop-shadow-[0_0_20px_rgba(251,146,60,0.95)]",  particle: "bg-orange-400"  },
  holy:      { sigil: "✨", aura: "bg-amber-200/60 blur-2xl",   tint: "drop-shadow-[0_0_24px_rgba(254,243,199,1)]",    particle: "bg-amber-200"   },
  smoke:     { sigil: "🌫️", aura: "bg-slate-500/40 blur-3xl",  tint: "drop-shadow-[0_0_20px_rgba(148,163,184,0.9)]",  particle: "bg-slate-400"   },
  earth:     { sigil: "🌋", aura: "bg-amber-700/40 blur-2xl",   tint: "drop-shadow-[0_0_18px_rgba(180,83,9,0.9)]",     particle: "bg-amber-700"   },
  slash:     { sigil: "⚔️", aura: "bg-red-500/30 blur-2xl",    tint: "drop-shadow-[0_0_16px_rgba(239,68,68,0.85)]",   particle: "bg-red-400"     },
  lightning: { sigil: "⚡", aura: "bg-yellow-300/40 blur-2xl",  tint: "drop-shadow-[0_0_22px_rgba(253,224,71,0.95)]",  particle: "bg-yellow-300"  },
  ultimate:  { sigil: "💫", aura: "bg-fuchsia-500/40 blur-3xl", tint: "drop-shadow-[0_0_28px_rgba(232,121,249,1)]",    particle: "bg-fuchsia-300" },
};

// ───────── Public components ─────────
export function SkillVfxBurst({ category, isUltimate, sizeClass }) {
  const conf = VFX_PRESETS[category] || VFX_PRESETS.slash;
  const auraSize = sizeClass || "w-40 h-40";
  return (
    <div
      className="pointer-events-none absolute inset-0 flex items-center justify-center vfx-pop"
      data-testid={`skill-vfx-${category}`}
    >
      <div className={`absolute ${auraSize} rounded-full ${conf.aura} vfx-aura ${isUltimate ? "vfx-aura-ult" : ""}`} />
      <div className={`relative text-6xl ${conf.tint} vfx-sigil`}>{conf.sigil}</div>
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className={`absolute w-2 h-8 rounded-full ${conf.particle} vfx-particle`}
          style={{ transform: `rotate(${i * 72}deg) translateY(-60px)`, animationDelay: `${i * 40}ms` }}
        />
      ))}
    </div>
  );
}

export function SpriteFighter({ actor, side, role, effect, isEnemy, variant = "full" }) {
  // 🧭 Oct 8 2026 — Avatar-dedup fix: when the authored sprite image
  // loads successfully, hide the emoji fallback so a character never
  // shows TWO different designs on the same card. If the image URL is
  // missing or 404s, we flip back to the emoji glyph.
  // Hooks MUST be called unconditionally — before any early return.
  const url = actor ? spriteUrlFor(actor, isEnemy) : null;
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError]   = useState(false);
  useEffect(() => {
    setImgLoaded(false);
    setImgError(false);
  }, [url]);

  if (!actor) return null;
  const spriteAnimClass = spriteAnimFor(role, effect);
  const vfxCategory = effect && role === "attacker"
    ? skillVfxCategory(effect.kind, effect.appliedStatusId)
    : null;
  const showSkillVfx = effect && role === "attacker";
  const showFallback = !url || imgError || !imgLoaded;

  const stageClass = variant === "compact"
    ? "relative w-full h-20 rounded-lg bg-gradient-to-b from-black/80 via-black/40 to-black/80 overflow-hidden border border-white/5"
    : "relative w-full h-44 sm:h-52 rounded-xl bg-gradient-to-b from-black/80 via-black/50 to-black/80 overflow-hidden border border-white/5";

  const fallbackSize = variant === "compact" ? "text-5xl" : "text-7xl";
  const fallbackGlyph = actor.icon || (isEnemy ? "🎯" : "🦸");

  return (
    <div className={stageClass} data-testid={`sprite-stage-${side}`}>
      <div className={`absolute ${variant === "compact" ? "bottom-1 w-16 h-1" : "bottom-2 w-32 h-3"} left-1/2 -translate-x-1/2 rounded-full bg-black/60 blur-md`} />
      <div className={`absolute inset-0 flex items-end justify-center pb-1 ${spriteAnimClass}`}>
        {showFallback && (
          <div
            className={`absolute inset-0 flex items-end justify-center pb-1 ${fallbackSize} drop-shadow-[0_0_10px_rgba(0,0,0,0.6)]`}
            data-testid={`sprite-fallback-${side}`}
          >
            {fallbackGlyph}
          </div>
        )}
        {url && !imgError && (
          <img
            src={url}
            alt={actor.name}
            className={`relative h-full w-auto object-contain drop-shadow-[0_4px_8px_rgba(0,0,0,0.7)] transition-opacity ${imgLoaded ? "opacity-100" : "opacity-0"}`}
            style={{ mixBlendMode: "screen" }}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
            data-testid={`sprite-img-${side}`}
          />
        )}
      </div>
      {showSkillVfx && vfxCategory && (
        <SkillVfxBurst
          key={effect.key}
          category={vfxCategory}
          isUltimate={effect.vfx?.includes("ultimate")}
          sizeClass={variant === "compact" ? "w-16 h-16" : undefined}
        />
      )}
    </div>
  );
}

// ───────── Keyframes — inject once per surface ─────────
const KEYFRAMES_ID = "__omni_sprite_styles__";
const KEYFRAMES_CSS = `
@keyframes attLungeDown { 0%{transform:translateY(0) scale(1)} 35%{transform:translateY(28px) scale(1.04)} 70%{transform:translateY(-6px) scale(1.02)} 100%{transform:translateY(0) scale(1)} }
@keyframes attLungeUp   { 0%{transform:translateY(0) scale(1)} 35%{transform:translateY(-28px) scale(1.04)} 70%{transform:translateY(6px) scale(1.02)} 100%{transform:translateY(0) scale(1)} }
@keyframes hitShake     { 0%{transform:translateX(0)} 15%{transform:translateX(-6px) rotate(-0.6deg)} 35%{transform:translateX(7px) rotate(0.5deg)} 55%{transform:translateX(-4px) rotate(-0.3deg)} 75%{transform:translateX(3px)} 100%{transform:translateX(0)} }
@keyframes hitShakeCrit { 0%{transform:translateX(0) scale(1)} 15%{transform:translateX(-10px) scale(1.02)} 35%{transform:translateX(12px) scale(1.03)} 55%{transform:translateX(-7px) scale(1.01)} 75%{transform:translateX(5px)} 100%{transform:translateX(0) scale(1)} }
@keyframes hitTrue      { 0%{transform:scale(1); filter:hue-rotate(0deg) saturate(1)} 25%{transform:scale(1.06); filter:hue-rotate(280deg) saturate(2)} 60%{transform:scale(0.98); filter:hue-rotate(280deg) saturate(1.4)} 100%{transform:scale(1); filter:hue-rotate(0deg) saturate(1)} }
@keyframes selfPulse    { 0%{transform:scale(1); box-shadow:0 0 0 0 rgba(16,185,129,0)} 40%{transform:scale(1.025); box-shadow:0 0 22px 4px rgba(16,185,129,0.45)} 100%{transform:scale(1); box-shadow:0 0 0 0 rgba(16,185,129,0)} }
@keyframes selfPulseAbs { 0%{transform:scale(1); box-shadow:0 0 0 0 rgba(103,232,249,0)} 40%{transform:scale(1.025); box-shadow:0 0 22px 4px rgba(103,232,249,0.45)} 100%{transform:scale(1); box-shadow:0 0 0 0 rgba(103,232,249,0)} }
@keyframes flashQuick   { 0%{opacity:0} 25%{opacity:1} 100%{opacity:0} }
@keyframes spriteIdle   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3px)} }
@keyframes spriteAttackLunge { 0%{transform:translateX(0) scale(1)} 30%{transform:translateX(-8px) scale(0.96)} 55%{transform:translateX(20px) scale(1.08) rotate(-3deg)} 80%{transform:translateX(4px) scale(1.02)} 100%{transform:translateX(0) scale(1)} }
@keyframes spriteHurt   { 0%{transform:translateX(0) rotate(0); filter:brightness(1)} 20%{transform:translateX(8px) rotate(2deg); filter:brightness(1.6) saturate(0.7)} 50%{transform:translateX(-5px) rotate(-1deg); filter:brightness(1.2)} 100%{transform:translateX(0) rotate(0); filter:brightness(1)} }
@keyframes spriteHurtCrit { 0%{transform:translateX(0) scale(1) rotate(0); filter:brightness(1)} 15%{transform:translateX(14px) scale(0.94) rotate(4deg); filter:brightness(2.2) saturate(0.5) drop-shadow(0 0 12px #fbbf24)} 45%{transform:translateX(-8px) scale(1.03) rotate(-2deg); filter:brightness(1.4)} 100%{transform:translateX(0) scale(1) rotate(0); filter:brightness(1)} }
@keyframes spriteHurtTrue { 0%{transform:scale(1); filter:hue-rotate(0deg) brightness(1)} 25%{transform:scale(0.9); filter:hue-rotate(290deg) brightness(1.8) saturate(2.5)} 60%{transform:scale(1.04); filter:hue-rotate(290deg) brightness(1.3)} 100%{transform:scale(1); filter:hue-rotate(0deg) brightness(1)} }
@keyframes spriteHealGlow   { 0%{filter:brightness(1)} 50%{filter:brightness(1.4) drop-shadow(0 0 18px #34d399)} 100%{filter:brightness(1)} }
@keyframes spriteAbsorbGlow { 0%{filter:brightness(1)} 50%{filter:brightness(1.4) drop-shadow(0 0 18px #22d3ee)} 100%{filter:brightness(1)} }
@keyframes vfxPop      { 0%{opacity:0; transform:scale(0.6)} 25%{opacity:1; transform:scale(1.1)} 80%{opacity:1; transform:scale(1.05)} 100%{opacity:0; transform:scale(1.2)} }
@keyframes vfxAura     { 0%{opacity:0; transform:scale(0.4)} 50%{opacity:0.9; transform:scale(1.2)} 100%{opacity:0; transform:scale(1.8)} }
@keyframes vfxAuraUlt  { 0%{opacity:0; transform:scale(0.4) rotate(0deg)} 50%{opacity:1; transform:scale(1.4) rotate(180deg)} 100%{opacity:0; transform:scale(2.2) rotate(360deg)} }
@keyframes vfxSigil    { 0%{opacity:0; transform:scale(0.3) rotate(-20deg)} 30%{opacity:1; transform:scale(1.3) rotate(8deg)} 65%{opacity:1; transform:scale(1) rotate(-4deg)} 100%{opacity:0; transform:scale(1.4) rotate(0deg)} }
@keyframes vfxParticle { 0%{opacity:0; transform:rotate(var(--r,0deg)) translateY(0) scale(1)} 30%{opacity:1} 100%{opacity:0; transform:rotate(var(--r,0deg)) translateY(-160px) scale(0.4)} }
@keyframes chipPing    { 0%{transform:scale(0.7); opacity:0} 60%{transform:scale(1.15); opacity:1} 100%{transform:scale(1); opacity:1} }
@keyframes dmgFloat    { 0%{opacity:0; transform:translateY(8px) scale(0.85)} 20%{opacity:1; transform:translateY(-2px) scale(1.1)} 70%{opacity:1; transform:translateY(-22px) scale(1)} 100%{opacity:0; transform:translateY(-44px) scale(0.95)} }
.attacker-lunge-down { animation: attLungeDown 0.55s ease-out; }
.attacker-lunge-up   { animation: attLungeUp   0.55s ease-out; }
.target-hit-shake    { animation: hitShake     0.5s  ease-in-out; }
.target-hit-crit     { animation: hitShakeCrit 0.6s  ease-in-out; }
.target-hit-true     { animation: hitTrue      0.7s  ease-in-out; }
.self-pulse-heal     { animation: selfPulse    0.7s  ease-in-out; }
.self-pulse-absorb   { animation: selfPulseAbs 0.7s  ease-in-out; }
.animate-flash-quick { animation: flashQuick   0.55s ease-out forwards; }
.status-chip-ping    { animation: chipPing     0.35s ease-out; }
.dmg-float           { animation: dmgFloat     0.9s  ease-out forwards; }
.sprite-idle         { animation: spriteIdle      2.6s ease-in-out infinite; }
.sprite-attack-lunge { animation: spriteAttackLunge 0.6s ease-out; }
.sprite-hurt         { animation: spriteHurt       0.55s ease-in-out; }
.sprite-hurt-crit    { animation: spriteHurtCrit   0.65s ease-in-out; }
.sprite-hurt-true    { animation: spriteHurtTrue   0.7s  ease-in-out; }
.sprite-heal-glow    { animation: spriteHealGlow   0.7s  ease-in-out; }
.sprite-absorb-glow  { animation: spriteAbsorbGlow 0.7s  ease-in-out; }
.vfx-pop             { animation: vfxPop      0.85s ease-out forwards; }
.vfx-aura            { animation: vfxAura     0.85s ease-out forwards; }
.vfx-aura-ult        { animation: vfxAuraUlt  0.95s ease-out forwards; }
.vfx-sigil           { animation: vfxSigil    0.85s ease-out forwards; }
.vfx-particle        { animation: vfxParticle 0.85s ease-out forwards; }
`;

export function SpriteStyles() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById(KEYFRAMES_ID)) return;
    const tag = document.createElement("style");
    tag.id = KEYFRAMES_ID;
    tag.textContent = KEYFRAMES_CSS;
    document.head.appendChild(tag);
  }, []);
  return null;
}
